/**
 * @file Browser acquisition capture + first-touch persistence for the
 *       sign-in surface.
 * @module @labcraft/core/frontend/acquisition
 *
 * `BrowserAcquisition` reads the two browser-side acquisition signals —
 * `document.referrer` and the landing URL's query params — hands them to
 * the pure {@link CoreAnalytics.classifyAcquisition} classifier (a granular
 * open `source` + a derived coarse `category` + the sanitized campaign
 * name), and persists the **first meaningful touch** in `localStorage` so
 * a campaign that drove an anonymous multi-page visit survives to the
 * sign-in surface. Both fields are what the sign-in surfaces forward to the
 * account daemon, which writes them to `learners.acquisition_*` on first
 * sign-up (umbrella #1213, Phase 1 / #1214).
 *
 * ## Why persist — the multi-page-browsing leak
 *
 * A visitor lands on a UTM-tagged page (a blog post, a manifesto share),
 * browses a few internal pages, and only then hits the sign-in CTA. By the
 * time they sign in, `window.location.search` no longer carries the tags —
 * classifying live at sign-in would credit the internal (untagged) page,
 * losing the campaign. So {@link BrowserAcquisition.captureFirstTouch} runs
 * on **every** page boot and persists the classification the first time it
 * sees a real touch; {@link BrowserAcquisition.read} at sign-in returns the
 * stored value (falling back to a live classification when storage is
 * empty or blocked). This replaces the earlier per-hop `?utm_*`-forwarding
 * of the `/#start` CTA (which only covered the CTA links, and only across
 * that one hop).
 *
 * First-touch, with one caveat: a **real** channel (`campaign` / `organic`
 * / `referral`) is captured once and never overwritten, but a stored
 * `direct` is a placeholder — a later real touch upgrades it. So a visitor
 * who browsed directly once, then returned via a campaign and signed up,
 * is credited to the campaign that actually drove the sign-up, not the
 * stale `direct`.
 *
 * ## PII invariant
 *
 * The raw referrer + raw `utm_*` values never leave the browser and are
 * never persisted — {@link CoreAnalytics.classifyAcquisition} collapses
 * them at the capture boundary, and only its sanitized `source` (host-only,
 * charset-bounded), coarse `category`, and sanitized `campaign` are stored
 * or exposed. The stored value is a full {@link Acquisition} (already
 * sanitized), re-validated against {@link AcquisitionSchema} on read so a
 * tampered `localStorage` entry can never surface an unsanitized value.
 *
 * Lives in `@labcraft/core/frontend` (not `@labcraft/ui`), alongside
 * {@link browserTimezone}, because the transport-only `src/client`s that
 * build the OAuth redirect URL cannot import React, and both the client
 * OAuth methods and the email-signin hook need this leaf. Both www and lab
 * capture on boot + read at sign-in through this one class.
 *
 * Best-effort throughout: a runtime with no `window` / `document` (SSR, a
 * test harness) or a `localStorage` that throws (private mode, quota,
 * disabled) degrades to `undefined` / a live classification — nothing here
 * ever blocks sign-in.
 */

import {
  AcquisitionSchema,
  CoreAnalytics,
  type Acquisition,
} from '../shared/core_analytics.ts'

/**
 * Static home for the browser-side acquisition capture: a live classifier,
 * a first-touch `localStorage` persister, and a stored-or-live reader. Pure
 * browser-edge helpers with no instance state, so `static` per
 * `.agents/rules/typescript.md` § "Functions belong on classes"; grouped on
 * one class so "how the sign-in surface captures + reads acquisition" has a
 * single discovery surface.
 */
export class BrowserAcquisition {
  /**
   * `localStorage` key the first-touch capture is persisted under. Prefixed
   * `lc_` per `.agents/rules/naming.md` (snake_case wire identifiers). The
   * value is a JSON-serialized {@link Acquisition}.
   */
  static readonly STORAGE_KEY = 'lc_acquisition'

  /**
   * The visitor's acquisition, classified live from the current browser
   * signals (`document.referrer` + `window.location.search`), or
   * `undefined` when there is no `window` to read from (SSR / non-browser
   * runtime). The raw referrer + utm strings are inspected only inside
   * {@link CoreAnalytics.classifyAcquisition} and are never returned.
   *
   * @returns The live {@link Acquisition}, or `undefined` off-browser.
   */
  static live(): Acquisition | undefined {
    try {
      return CoreAnalytics.classifyAcquisition({
        referrer: document.referrer,
        params: new URLSearchParams(window.location.search),
      })
    } catch {
      return undefined
    }
  }

  /**
   * Persist the first meaningful acquisition touch. Called on every page
   * boot (www's island bootloader, lab's SPA entry): classifies the live
   * signals and writes them to `localStorage` when nothing is stored yet,
   * or when a real channel supersedes a stored `direct` placeholder (see
   * the module header's first-touch caveat). A no-op when a real touch is
   * already stored, when there is nothing to classify, or when
   * `localStorage` is unavailable — capture is strictly best-effort.
   */
  static captureFirstTouch(): void {
    const live = BrowserAcquisition.live()
    if (!live) return

    const stored = BrowserAcquisition.stored()
    // First *real* (non-`direct`) touch wins and is never overwritten; a
    // stored `direct` is a placeholder a later real channel upgrades. So
    // persist only when nothing is stored, or a real touch supersedes a
    // stored `direct`.
    const shouldStore =
      !stored || (stored.category === 'direct' && live.category !== 'direct')
    if (!shouldStore) return

    try {
      window.localStorage.setItem(
        BrowserAcquisition.STORAGE_KEY,
        JSON.stringify(live),
      )
    } catch {
      // localStorage unavailable (private mode, quota, disabled) — the
      // capture is best-effort; sign-in still works, it just classifies
      // live at that point.
    }
  }

  /**
   * The acquisition to forward at sign-in: the persisted first-touch value
   * when present, else a live classification of the current signals. The
   * stored value is re-validated against {@link AcquisitionSchema}, so a
   * malformed or tampered `localStorage` entry falls through to the live
   * classification rather than surfacing a bad value.
   *
   * @returns The stored-or-live {@link Acquisition}, or `undefined`
   *   off-browser with nothing stored.
   */
  static read(): Acquisition | undefined {
    return BrowserAcquisition.stored() ?? BrowserAcquisition.live()
  }

  /**
   * Read + validate the persisted first-touch acquisition, or `null` when
   * absent, unparseable, or `localStorage` is unavailable. Private: callers
   * reach persistence via {@link captureFirstTouch} / {@link read}, which
   * own the write-once and stored-or-live semantics.
   *
   * @returns The validated stored {@link Acquisition}, or `null`.
   */
  private static stored(): Acquisition | null {
    try {
      const raw = window.localStorage.getItem(BrowserAcquisition.STORAGE_KEY)
      if (!raw) return null
      const parsed = AcquisitionSchema.safeParse(JSON.parse(raw))
      return parsed.success ? parsed.data : null
    } catch {
      return null
    }
  }
}
